"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import apiFetch from "@/lib/fetch";

interface CompanyData {
  id: number;
  discount: number;
  title: string;
  slug: string;
  shortDescription: string;
  logo: string;
}

interface CompanyResponse {
  data: CompanyData[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

interface CompanySummary {
  campaigns: number;
  venues: number;
  companies: number;
  events: number;
  nearby: {
    campaigns: number;
    venues: number;
  };
}

interface CompanyApiResponse {
  response: CompanyResponse;
  summary: CompanySummary;
}

interface UseCompaniesParams {
  service?: string;
  district?: string;
  venue?: string;
}

export function useCompanies({ service, district, venue }: UseCompaniesParams = {}) {
  return useInfiniteQuery<CompanyApiResponse>({
    queryKey: ["companies", service, district, venue],
    queryFn: async ({ pageParam = 0 }) => {
      let url = `company/list?index=${pageParam}`;
      if (service) url += `&service=${service}`;
      if (district) url += `&district=${district}`;
      if (venue) url += `&venue=${venue}`;

      return await apiFetch({ route: url });
    },
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage.response;
      return currentPage < totalPages - 1 ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
  });
}

interface VenueCompany {
  id: number;
  discount: number;
  title: string;
  logo: string;
  slug: string;
  shortDescription: string;
  venueCompanyDescription: string;
}

interface VenueCompaniesResponse {
  data: VenueCompany[];
  usageData: any;
}

export function useVenueCompanies(venueId: number) {
  return useInfiniteQuery<VenueCompaniesResponse>({
    queryKey: ["venue-companies", venueId],
    queryFn: async ({ pageParam = 0 }) => {
      return await apiFetch({
        route: `company/venue/${venueId}?index=${pageParam}`,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const DEFAULT_DATA_LIMIT = 20;
      return lastPage.data.length < DEFAULT_DATA_LIMIT ? undefined : allPages.length;
    },
    initialPageParam: 0,
    enabled: !!venueId,
  });
}